import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/shop.scss";

const SORT_OPTIONS = [
  ["featured", "Featured"],
  ["best-selling", "Best selling"],
  ["name-asc", "Alphabetically, A–Z"],
  ["name-desc", "Alphabetically, Z–A"],
  ["price-asc", "Price, low to high"],
  ["price-desc", "Price, high to low"],
  ["oldest", "Date, old to new"],
  ["newest", "Date, new to old"],
];

const getProductImage = (product, index = 0) =>
  product?.images?.[index] || product?.images?.[0] || "";

const getSizes = (product) =>
  Array.isArray(product?.size) && product.size.length
    ? product.size
    : ["One Size"];

const getColours = (product) =>
  Array.isArray(product?.colors) && product.colors.length
    ? product.colors
    : ["Default"];

const isOutOfStock = (product) =>
  product?.stock === false ||
  Number(product?.stock) === 0 ||
  String(product?.stock).toLowerCase() === "out of stock";

const getBadge = (product) => {
  const previousPrice = product?.compareAtPrice ?? product?.originalPrice;
  if (Number(previousPrice) > Number(product?.price)) return "SALE";
  if (product?.newArrival) return "NEW";
  return "";
};

const compareProducts = (sort) => (first, second) => {
  const firstPrice = Number(first.price) || 0;
  const secondPrice = Number(second.price) || 0;
  const firstDate = Date.parse(first.createdAt || first.releaseDate || "") || 0;
  const secondDate = Date.parse(second.createdAt || second.releaseDate || "") || 0;
  const firstId = Number(first.id) || 0;
  const secondId = Number(second.id) || 0;

  switch (sort) {
    case "best-selling":
      return (
        (Number(second.sales) || Number(Boolean(second.bestSeller))) -
          (Number(first.sales) || Number(Boolean(first.bestSeller))) ||
        firstId - secondId
      );
    case "name-asc":
      return first.name.localeCompare(second.name);
    case "name-desc":
      return second.name.localeCompare(first.name);
    case "price-asc":
      return firstPrice - secondPrice;
    case "price-desc":
      return secondPrice - firstPrice;
    case "oldest":
      return firstDate - secondDate || firstId - secondId;
    case "newest":
      return (
        Number(Boolean(second.newArrival)) - Number(Boolean(first.newArrival)) ||
        secondDate - firstDate ||
        secondId - firstId
      );
    default:
      return (
        Number(Boolean(second.featured)) - Number(Boolean(first.featured)) ||
        firstId - secondId
      );
  }
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [quickAddId, setQuickAddId] = useState(null);
  const [addedId, setAddedId] = useState(null);
  const { addToCart, wishlist, toggleWishlist } = useContext(CartContext);

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };

    document.body.classList.add("menu-open");
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("menu-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [filtersOpen]);

  const query = searchParams.get("q") || "";
  const activeCategory = searchParams.get("category") || "";
  const activeSize = searchParams.get("size") || "";
  const wishlistOnly = ["1", "true"].includes(
    (searchParams.get("wishlist") || "").toLowerCase()
  );
  const requestedSort = searchParams.get("sort") || "featured";
  const activeSort = SORT_OPTIONS.some(([value]) => value === requestedSort)
    ? requestedSort
    : "featured";

  const categories = useMemo(
    () =>
      [...new Set(productData.map((product) => product.category).filter(Boolean))].sort(
        (first, second) => first.localeCompare(second)
      ),
    []
  );

  const sizes = useMemo(() => [...new Set(productData.flatMap(getSizes))], []);

  const products = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return productData
      .filter((product) => {
        const searchableText = [
          product.name,
          product.category,
          product.sku,
          ...(product.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return (
          (!normalizedQuery || searchableText.includes(normalizedQuery)) &&
          (!activeCategory ||
            product.category?.toLowerCase() === activeCategory.toLowerCase()) &&
          (!activeSize || getSizes(product).includes(activeSize)) &&
          (!wishlistOnly || wishlist.includes(product.id))
        );
      })
      .sort(compareProducts(activeSort));
  }, [activeCategory, activeSize, activeSort, query, wishlist, wishlistOnly]);

  const activeFilterCount =
    Number(Boolean(query)) +
    Number(Boolean(activeCategory)) +
    Number(Boolean(activeSize)) +
    Number(wishlistOnly);

  const updateParam = (key, value) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) nextParams.set(key, value);
    else nextParams.delete(key);

    setSearchParams(nextParams, { replace: true });
  };

  const clearFilters = () => {
    const nextParams = new URLSearchParams();
    if (activeSort !== "featured") nextParams.set("sort", activeSort);
    setSearchParams(nextParams, { replace: true });
  };

  const handleQuickAdd = (product, size) => {
    addToCart(product.id, 1, size, getColours(product)[0]);
    setAddedId(product.id);
    setQuickAddId(null);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="shop">
      <header className="shop__hero">
        <div className="page-shell shop__hero-inner">
          <p>{siteConfig.wordmark}</p>
          <div className="shop__title-line">
            <h1>Shop</h1>
            <span>
              {products.length} {products.length === 1 ? "item" : "items"}
            </span>
          </div>
          <p className="shop__intro">{siteConfig.description}</p>
        </div>
      </header>

      <section className="shop__toolbar" aria-label="Product controls">
        <div className="page-shell shop__toolbar-inner">
          <button
            className="shop__filter-toggle"
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-expanded={filtersOpen}
            aria-controls="shop-filters"
          >
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="shop__filter-count">{activeFilterCount}</span>
            )}
            <span aria-hidden="true">＋</span>
          </button>

          <label className="shop__sort">
            <span>Sort by</span>
            <select
              value={activeSort}
              onChange={(event) => updateParam("sort", event.target.value)}
            >
              {SORT_OPTIONS.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {activeFilterCount > 0 && (
        <div className="page-shell shop__active-filters" aria-label="Active filters">
          {query && (
            <button type="button" onClick={() => updateParam("q", "")}>
              Search: {query} <span aria-hidden="true">×</span>
            </button>
          )}
          {activeCategory && (
            <button type="button" onClick={() => updateParam("category", "")}>
              {activeCategory} <span aria-hidden="true">×</span>
            </button>
          )}
          {activeSize && (
            <button type="button" onClick={() => updateParam("size", "")}>
              Size {activeSize} <span aria-hidden="true">×</span>
            </button>
          )}
          {wishlistOnly && (
            <button type="button" onClick={() => updateParam("wishlist", "")}>
              Wishlist <span aria-hidden="true">×</span>
            </button>
          )}
          <button className="shop__clear-link" type="button" onClick={clearFilters}>
            Clear all
          </button>
        </div>
      )}

      <section className="page-shell shop__catalog" aria-live="polite">
        {products.length ? (
          <div className="shop__grid">
            {products.map((product) => {
              const badge = getBadge(product);
              const outOfStock = isOutOfStock(product);
              const isWishlisted = wishlist.includes(product.id);
              const quickAddOpen = quickAddId === product.id;
              const secondaryImage = product?.images?.[1] || "";

              return (
                <article
                  className={`catalog-card${secondaryImage ? " has-secondary" : ""}`}
                  key={product.id}
                >
                  <div className="catalog-card__media">
                    <Link
                      to={`/shopDetails/${product.id}`}
                      onClick={scrollToTop}
                      aria-label={`View ${product.name}`}
                    >
                      <img
                        className="catalog-card__image catalog-card__image--primary"
                        src={getProductImage(product)}
                        alt={product.name}
                        loading="lazy"
                      />
                      {secondaryImage && (
                        <img
                          className="catalog-card__image catalog-card__image--secondary"
                          src={secondaryImage}
                          alt=""
                          loading="lazy"
                        />
                      )}
                    </Link>

                    {badge && (
                      <span
                        className={`catalog-card__badge${
                          badge === "SALE" ? " is-sale" : ""
                        }`}
                      >
                        {badge}
                      </span>
                    )}

                    <button
                      className={`catalog-card__wish${isWishlisted ? " is-active" : ""}`}
                      type="button"
                      onClick={() => toggleWishlist(product.id)}
                      aria-label={`${isWishlisted ? "Remove" : "Add"} ${
                        product.name
                      } ${isWishlisted ? "from" : "to"} wishlist`}
                      aria-pressed={isWishlisted}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 20.6 3.7 12.5C1.4 10.3 1.4 6.7 3.5 4.6a5.3 5.3 0 0 1 7.5 0l1 1 1-1a5.3 5.3 0 0 1 7.5 0c2.1 2.1 2.1 5.7-.2 7.9L12 20.6Z" />
                      </svg>
                    </button>

                    <div
                      className={`catalog-card__quick${quickAddOpen ? " is-open" : ""}`}
                    >
                      {quickAddOpen ? (
                        <div className="catalog-card__sizes">
                          <div className="catalog-card__sizes-heading">
                            <span>Select a size</span>
                            <button
                              type="button"
                              onClick={() => setQuickAddId(null)}
                              aria-label="Close quick add"
                            >
                              ×
                            </button>
                          </div>
                          <div className="catalog-card__size-grid">
                            {getSizes(product).map((size) => (
                              <button
                                type="button"
                                key={size}
                                onClick={() => handleQuickAdd(product, size)}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <button
                          className="catalog-card__quick-toggle"
                          type="button"
                          disabled={outOfStock}
                          onClick={() => setQuickAddId(product.id)}
                        >
                          {outOfStock
                            ? "Sold out"
                            : addedId === product.id
                            ? "Added to cart"
                            : "Quick add"}
                        </button>
                      )}
                    </div>
                  </div>

                  <Link
                    className="catalog-card__details"
                    to={`/shopDetails/${product.id}`}
                    onClick={scrollToTop}
                  >
                    <h2>{product.name}</h2>
                    <div>
                      {product.sku && <span>{product.sku}</span>}
                      <strong>{formatPrice(product.price)}</strong>
                    </div>
                  </Link>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="shop__empty">
            <p>No pieces match your selection.</p>
            <h2>Try another cut, size or search.</h2>
            <button type="button" onClick={clearFilters}>
              Clear all filters
            </button>
          </div>
        )}
      </section>

      <div
        className={`shop-filter-overlay${filtersOpen ? " is-open" : ""}`}
        aria-hidden={!filtersOpen}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setFiltersOpen(false);
        }}
      >
        <aside
          className="shop-filter-drawer"
          id="shop-filters"
          aria-label="Product filters"
        >
          <header className="shop-filter-drawer__header">
            <div>
              <span>Filters</span>
              <small>{products.length} results</small>
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              aria-label="Close filters"
            >
              ×
            </button>
          </header>

          <div className="shop-filter-drawer__body">
            <div className="shop-filter-group shop-filter-group--search">
              <label htmlFor="shop-search">Search the collection</label>
              <div>
                <input
                  id="shop-search"
                  type="search"
                  value={query}
                  onChange={(event) => updateParam("q", event.target.value)}
                  placeholder="Jackets, denim, shirts…"
                />
                <span aria-hidden="true">⌕</span>
              </div>
            </div>

            <fieldset className="shop-filter-group">
              <legend>Category</legend>
              <div className="shop-filter-group__chips">
                {categories.map((category) => (
                  <button
                    className={activeCategory === category ? "is-active" : ""}
                    type="button"
                    key={category}
                    onClick={() =>
                      updateParam(
                        "category",
                        activeCategory === category ? "" : category
                      )
                    }
                  >
                    {category}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="shop-filter-group">
              <legend>Size</legend>
              <div className="shop-filter-group__chips shop-filter-group__chips--sizes">
                {sizes.map((size) => (
                  <button
                    className={activeSize === size ? "is-active" : ""}
                    type="button"
                    key={size}
                    onClick={() =>
                      updateParam("size", activeSize === size ? "" : size)
                    }
                  >
                    {size}
                  </button>
                ))}
              </div>
            </fieldset>

            <label className="shop-filter-wishlist">
              <input
                type="checkbox"
                checked={wishlistOnly}
                onChange={(event) =>
                  updateParam("wishlist", event.target.checked ? "1" : "")
                }
              />
              <span>Show wishlist only</span>
              <small>{wishlist.length}</small>
            </label>
          </div>

          <footer className="shop-filter-drawer__footer">
            <button type="button" onClick={clearFilters}>
              Clear all
            </button>
            <button type="button" onClick={() => setFiltersOpen(false)}>
              View {products.length} {products.length === 1 ? "item" : "items"}
            </button>
          </footer>
        </aside>
      </div>
    </main>
  );
}

export default Shop;
